import { IconClose } from '@tvg/fuse-icons';
import { SrOnly } from '@tvg/fuse-utils';
import PropTypes from 'prop-types';
import React from 'react';
import {
	StyledAlert,
	StyledAlertContent,
	StyledAlertContentContainer,
	StyledAlertHeading,
	StyledAlertMessage,
	StyledCloseButtonContainer,
	StyledIconButton,
} from './styled-alert';

const { forwardRef, useCallback } = React;

const propTypes = {
	banner: PropTypes.bool,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	closeCallback: PropTypes.func,
	dataTestId: PropTypes.string,
	heading: PropTypes.string,
	message: PropTypes.string,
	showIcon: PropTypes.bool,
	size: PropTypes.oneOf(['small', 'responsive']),
	variant: PropTypes.oneOf(['warning', 'error', 'success', 'information']),
};
const defaultProps = {
	banner: false,
	children: undefined,
	closeCallback: () => {},
	dataTestId: undefined,
	heading: '',
	message: '',
	showIcon: true,
	size: 'responsive',
	variant: 'information',
};

const AlertComponent = forwardRef(
	(
		{
			banner,
			children,
			closeCallback,
			dataTestId,
			heading,
			message,
			showIcon,
			size,
			variant,
			...props
		},
		ref,
	) => {
		const { ...others } = props;
		const [isClosed, setIsClosed] = React.useState(false);
		const closeClickHandler = useCallback(
			(e) => {
				setIsClosed(true);
				closeCallback(e);
			},
			[isClosed],
		);

		return (
			!isClosed && (
				<StyledAlert
					banner={banner}
					data-testid={dataTestId || 'fuse-alert'}
					ref={ref}
					size={size}
					variant={variant}
					{...others}
				>
					<StyledAlertContentContainer
						xxs={11}
						l={10}
						variant={variant}
						showIcon={showIcon}
						size={size}
					>
						<StyledAlertContent>
							<SrOnly>An alert has occurred - {variant}</SrOnly>
							{heading && <StyledAlertHeading>{heading}</StyledAlertHeading>}
							{message && <StyledAlertMessage>{message}</StyledAlertMessage>}
							{children}
						</StyledAlertContent>
					</StyledAlertContentContainer>
					{banner && (
						<StyledCloseButtonContainer>
							<StyledIconButton
								icon={<IconClose />}
								callback={(e) => closeClickHandler(e)}
								srLabel={`Close ${variant} dialog`}
							/>
						</StyledCloseButtonContainer>
					)}
				</StyledAlert>
			)
		);
	},
);

AlertComponent.propTypes = propTypes;
AlertComponent.defaultProps = defaultProps;

export default AlertComponent;

export { AlertComponent as Alert };
